import classNames from 'classnames'
import { useCallback } from 'react'
import { Controller, FieldValues } from 'react-hook-form'

import { FormError } from '@/components'
import { TextInputProps } from '@/components/Form/types'
import { t } from '@/i18n'
import { getMaskedString } from '@/utils'

const TextInput = <T extends FieldValues>({
  className,
  name,
  label,
  isCarNumber,
  isEditing,
  isCurrency,
  currency,
  emptyValue,
  control,
  required,
  inputMask,
  onFieldChange,
  type,
  ...rest
}: TextInputProps<HTMLInputElement, T>) => {
  const onValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, onChange: any) => {
      const val = isCarNumber
        ? e.target.value.replace(/-/g, '')
        : e.target.value
      const processedVal = getMaskedString(val, isCarNumber, inputMask)

      if (inputMask || isCarNumber) {
        e.target.value = processedVal
      }
      onChange(e)
      onFieldChange?.(processedVal)
    },
    [inputMask, isCarNumber, onFieldChange],
  )

  if (!name) return null
  const renderField = useCallback(
    (v: any, onChange: any) => {
      const _v = ['string', 'number'].includes(typeof v) ? `${v}` : ''
      if (!isEditing) {
        if (emptyValue && [undefined, null, ''].includes(v)) {
          return <div className="empty-value">{emptyValue}</div>
        } else if (currency && isCurrency && !isNaN(+_v)) {
          return (
            <div className="display-value">
              {t('currency', {
                value: +v,
                currency,
              })}
            </div>
          )
        }
      }
      if (typeof v === 'boolean') {
        rest.checked = v
      }

      return (
        <input
          name={name}
          required={required}
          value={_v}
          onChange={e => onValueChange(e, onChange)}
          type={type}
          min={type === 'number' ? 0 : undefined}
          {...rest}
        />
      )
    },
    [
      currency,
      emptyValue,
      isCurrency,
      isEditing,
      name,
      onValueChange,
      required,
      type,
      rest,
    ],
  )
  return (
    <label className={className}>
      {!!label && (
        <span className={classNames('name', { required: !!required })}>
          {label} {!!required && <sup>*</sup>}
        </span>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            {renderField(value, onChange)}
            {isEditing && <FormError error={error?.message} />}
          </>
        )}
      />
    </label>
  )
}

export default TextInput
